import {useEffect, useMemo, useReducer} from 'react'
import {readServerChartPreference, writeServerChartPreference} from '../lib/chart/chartPreferenceStore.js'

const MODES = ['dark', 'light']
const valid = n => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100
const cacheKey = (userId, mode) => `erevis-interface-saturation-v1:${userId}:${mode}`
function cached(userId, mode) {
  try {
    const row = JSON.parse(localStorage.getItem(cacheKey(userId, mode)))
    if (userId && valid(row?.value)) return {value:row.value, pending:row.pending === true}
  } catch { /* Account reads still work without local storage. */ }
  return {value:100, pending:false}
}
function cache(userId, mode, value, pending) {
  if (userId) try { localStorage.setItem(cacheKey(userId, mode), JSON.stringify({value,pending})) } catch { /* best effort */ }
}
function legacyForOwner(userId, mode) {
  try {
    const key = 'erevis-interface-saturation-legacy-owner'
    const owner = localStorage.getItem(key)
    if (owner && owner !== userId) return null
    if (!owner) localStorage.setItem(key, userId)
    const raw = localStorage.getItem(`erevis-interface-saturation-${mode}`)
    const value = raw === null ? null : Number(raw)
    return valid(value) ? value : null
  } catch { return null }
}

// Separate rows prevent a dark-mode edit on one device from overwriting a
// light-mode edit on another. Only explicit edits or a one-time legacy import
// write; a failed read is never treated as a missing row.
export function useInterfaceSaturation(userId) {
  const [, render] = useReducer(n => n + 1, 0)
  const state = useMemo(() => {
    const s = {active:false, modes:{}}
    const notify = () => { if (s.active) render() }
    for (const mode of MODES) {
      const local = cached(userId, mode)
      const m = s.modes[mode] = {value:local.value, pending:local.pending ? local.value : null,
        status:userId ? 'loading' : 'unavailable', hydrated:false, loading:false,
        saving:false, revision:0, timer:null}
      const target = {userId, scope:'app-theme', preferenceKey:`interface-saturation-${mode}`}
      m.flush = async () => {
        if (!s.active || !userId || !m.hydrated || m.saving || m.pending === null) return
        m.saving = true
        m.status = 'saving'
        notify()
        while (s.active && m.pending !== null) {
          const value = m.pending
          m.pending = null
          const ok = await writeServerChartPreference({...target, preferences:{version:1,value}}).catch(() => false)
          if (!s.active) { m.saving = false; return }
          if (!ok) {
            if (m.pending === null) m.pending = value
            m.status = 'error'
            m.saving = false
            notify()
            return
          }
        }
        m.saving = false
        m.status = 'saved'
        cache(userId, mode, m.value, false)
        notify()
      }
      m.load = async () => {
        if (!s.active || !userId || m.loading || m.saving) return
        if (m.hydrated && m.pending !== null) { void m.flush(); return }
        const revision = m.revision
        m.loading = true
        try {
          const stored = await readServerChartPreference({...target, throwOnError:true})
          if (!s.active) return
          // Claim old browser-only choices once; later account switches cannot
          // inherit those unscoped keys. Existing server values always win.
          const legacy = legacyForOwner(userId, mode)
          if (revision === m.revision && m.pending === null) {
            if (stored === null && legacy !== null && !m.hydrated) {
              m.value = legacy
              m.pending = legacy
              cache(userId, mode, legacy, true)
            } else {
              m.value = stored?.version === 1 && valid(stored.value) ? stored.value : 100
              cache(userId, mode, m.value, false)
            }
          }
          m.hydrated = true
          m.status = m.saving || m.pending !== null ? 'saving' : stored === null ? 'ready' : 'saved'
          notify()
          void m.flush()
        } catch {
          if (s.active) { m.status = 'error'; notify() }
        } finally { m.loading = false }
      }
    }
    s.change = (mode, value) => {
      const m = s.modes[mode]
      if (!m || !valid(value) || !userId || !s.active) return
      m.value = value
      m.pending = value
      m.revision++
      m.status = 'saving'
      cache(userId, mode, value, true)
      clearTimeout(m.timer)
      m.timer = setTimeout(() => m.hydrated ? void m.flush() : void m.load(), 300)
      notify()
    }
    s.refresh = () => { for (const mode of MODES) void s.modes[mode].load() }
    return s
  }, [userId])

  useEffect(() => {
    state.active = true
    state.refresh()
    const visible = () => { if (document.visibilityState !== 'hidden') state.refresh() }
    const leave = event => {
      if (MODES.some(mode => state.modes[mode].pending !== null || state.modes[mode].saving)) {
        event.preventDefault(); event.returnValue = ''
      }
    }
    window.addEventListener('focus', visible)
    window.addEventListener('online', state.refresh)
    document.addEventListener('visibilitychange', visible)
    window.addEventListener('beforeunload', leave)
    const timer = setInterval(visible, 60_000)
    return () => {
      state.active = false
      MODES.forEach(mode => clearTimeout(state.modes[mode].timer))
      clearInterval(timer)
      window.removeEventListener('focus', visible)
      window.removeEventListener('online', state.refresh)
      document.removeEventListener('visibilitychange', visible)
      window.removeEventListener('beforeunload', leave)
    }
  }, [state])
  return {values:Object.fromEntries(MODES.map(mode => [mode,state.modes[mode].value])),
    status:Object.fromEntries(MODES.map(mode => [mode,state.modes[mode].status])), change:state.change, retry:state.refresh}
}
